import { SxProps, Theme } from "@mui/material";
import emptyStateTokens, {
  emptyStateAccents,
  EmptyStateAccent,
  EMPTY_PHONE_QUERY,
} from "./EmptyState.tokens";

const { well, tile, title, body, actions, link } = emptyStateTokens;

export type EmptyStateSize = "full" | "medium" | "compact";

/**
 * The well. Sits inside the container's existing border and paints the design's
 * recessed tint, so a table or card only has to swap its rows for this block.
 *
 * `framed` is for the other case — a screen whose whole body is the empty state,
 * with no card around it. There the well draws the border itself rather than
 * nesting a tinted panel inside an otherwise empty card.
 */
export const wellSx = (
  size: EmptyStateSize,
  framed = false,
): SxProps<Theme> => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  boxSizing: "border-box",
  width: "100%",
  bgcolor: well.bg,
  py: well.paddingY[size],
  px: well.paddingX[size],
  gap: well.gap[size],
  ...(framed
    ? { border: `1px solid ${well.border}`, borderRadius: well.radius }
    : {}),
  // The phone reference shortens the well; the two smaller sizes are already
  // at or below that height, so only `full` steps down.
  ...(size === "full"
    ? {
        [EMPTY_PHONE_QUERY]: {
          py: well.phonePaddingY,
          px: well.phonePaddingX,
          gap: well.phoneGap,
        },
      }
    : {}),
});

export const tileSx = (
  size: Exclude<EmptyStateSize, "compact">,
  accent: EmptyStateAccent,
): SxProps<Theme> => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  boxSizing: "border-box",
  width: tile.size[size],
  height: tile.size[size],
  borderRadius: tile.radius[size],
  bgcolor: emptyStateAccents[accent].tint,
  ...(size === "full"
    ? {
        [EMPTY_PHONE_QUERY]: {
          width: tile.size.phone,
          height: tile.size.phone,
          borderRadius: tile.radius.phone,
        },
      }
    : {}),
});

export const titleSx = (size: EmptyStateSize): SxProps<Theme> => ({
  color: title.color,
  fontSize: title.fontSize[size],
  fontWeight: title.fontWeight[size],
  letterSpacing: title.letterSpacing,
  lineHeight: "normal",
  ...(size === "full"
    ? { [EMPTY_PHONE_QUERY]: { fontSize: title.fontSize.phone } }
    : {}),
});

export const bodySx = (
  size: Exclude<EmptyStateSize, "compact">,
): SxProps<Theme> => ({
  maxWidth: body.maxWidth[size],
  color: body.color,
  fontSize: body.fontSize[size],
  lineHeight: body.lineHeight[size],
  textWrap: "pretty",
  ...(size === "full"
    ? {
        [EMPTY_PHONE_QUERY]: {
          fontSize: body.fontSize.phone,
          lineHeight: body.lineHeight.phone,
        },
      }
    : {}),
});

/**
 * Ghost then primary side by side. On the phone they stack full width and the
 * order flips — the primary reads first there, as in the 375px reference.
 */
export const actionRowSx: SxProps<Theme> = {
  display: "flex",
  gap: actions.gap,
  mt: actions.marginTop,
  [EMPTY_PHONE_QUERY]: {
    width: "100%",
    flexDirection: "column",
    gap: actions.phoneGap,
  },
};

const actionBaseSx = {
  height: actions.height,
  minHeight: actions.height,
  py: 0,
  border: "none",
  borderRadius: actions.radius,
  fontSize: actions.fontSize,
  fontWeight: actions.fontWeight,
  lineHeight: 1,
  textTransform: "none",
  boxShadow: "none",
  whiteSpace: "nowrap",
  flexShrink: 0,
  [EMPTY_PHONE_QUERY]: {
    order: 0,
    width: "100%",
    height: actions.phoneHeight,
    minHeight: actions.phoneHeight,
  },
} as const;

export const primaryActionSx: SxProps<Theme> = {
  ...actionBaseSx,
  px: actions.paddingX.primary,
  // The theme's `contained` override paints a `background` shorthand, which
  // silently wins over `bgcolor` here — set it inside `&.MuiButton-root`.
  "&.MuiButton-root": {
    background: actions.primaryBg,
    color: actions.primaryColor,
    boxShadow: "none",
    "&:hover": { background: actions.primaryBg, boxShadow: "none" },
  },
  // Primary reads first in the stacked phone layout.
  [EMPTY_PHONE_QUERY]: { ...actionBaseSx[EMPTY_PHONE_QUERY], order: 0 },
};

export const ghostActionSx: SxProps<Theme> = {
  ...actionBaseSx,
  px: actions.paddingX.ghost,
  "&.MuiButton-root": {
    background: actions.ghostBg,
    border: `1px solid ${actions.ghostBorder}`,
    color: actions.ghostColor,
    boxShadow: "none",
    "&:hover": { background: actions.ghostBg, boxShadow: "none" },
  },
  [EMPTY_PHONE_QUERY]: { ...actionBaseSx[EMPTY_PHONE_QUERY], order: 1 },
};

/** The medium size carries one small ghost button instead of a pair. */
export const smallActionSx: SxProps<Theme> = {
  alignSelf: "center",
  height: actions.smallHeight,
  minHeight: actions.smallHeight,
  px: actions.smallPaddingX,
  py: 0,
  mt: "2px",
  borderRadius: actions.smallRadius,
  fontSize: actions.smallFontSize,
  fontWeight: actions.fontWeight,
  lineHeight: 1,
  textTransform: "none",
  whiteSpace: "nowrap",
  "&.MuiButton-root": {
    background: actions.ghostBg,
    border: `1px solid ${actions.ghostBorder}`,
    color: actions.ghostColor,
    boxShadow: "none",
    "&:hover": { background: actions.ghostBg, boxShadow: "none" },
  },
};

/** The compact size carries a text link. */
export const linkActionSx: SxProps<Theme> = {
  p: 0,
  minWidth: 0,
  minHeight: 0,
  border: "none",
  background: "none",
  fontSize: link.fontSize,
  fontWeight: link.fontWeight,
  lineHeight: 1,
  textTransform: "none",
  color: link.color,
  "&.MuiButton-root": {
    background: "none",
    color: link.color,
    "&:hover": { background: "none", color: link.hoverColor },
  },
};
